import React, { useState, useEffect } from 'react';
import { PipelineNode, APIEndpointConfig, ModelConfig, LogicIfConfig } from '../../types/pipeline';
import { usePipelineBuilder } from '../../context/PipelineBuilderContext';
import { modelService, Model } from '../../services/modelService';
import '../../styles/pipelineBuilder.css';

interface PipelineNodeConfigPanelProps {
  node: PipelineNode | null;
}

export const PipelineNodeConfigPanel: React.FC<PipelineNodeConfigPanelProps> = ({ node }) => {
  const { updateNode } = usePipelineBuilder();

  if (!node) {
    return (
      <div className="config-panel">
        <div className="panel-empty">
          <p>Select a node to configure</p>
        </div>
      </div>
    );
  }

  const handleConfigChange = (key: string, value: any) => {
    updateNode(node.id, {
      config: {
        ...node.config,
        [key]: value,
      },
    });
  };

  const handleNameChange = (newName: string) => {
    updateNode(node.id, { name: newName });
  };

  const renderConfigUI = () => {
    switch (node.type) {
      case 'api_endpoint': {
        const config = node.config as APIEndpointConfig;
        return (
          <div className="config-fields">
            <div className="form-group">
              <label>URL</label>
              <input
                type="text"
                value={config.url || ''}
                onChange={(e) => handleConfigChange('url', e.target.value)}
                placeholder="https://api.example.com/endpoint"
              />
            </div>
            <div className="form-group">
              <label>Method</label>
              <select
                value={config.method || 'GET'}
                onChange={(e) => handleConfigChange('method', e.target.value)}
              >
                <option>GET</option>
                <option>POST</option>
                <option>PUT</option>
                <option>DELETE</option>
              </select>
            </div>
            <div className="form-group">
              <label>Timeout (ms)</label>
              <input
                type="number"
                value={config.timeout || 30000}
                onChange={(e) => handleConfigChange('timeout', parseInt(e.target.value))}
              />
            </div>
          </div>
        );
      }

      case 'model': {
        const config = node.config as ModelConfig;
        return <ModelNodeConfig config={config} onConfigChange={handleConfigChange} nodeId={node.id} />;
      }

      case 'logic_if': {
        const config = node.config as LogicIfConfig;
        return (
          <div className="config-fields">
            <div className="form-group">
              <label>Condition Variable</label>
              <input
                type="text"
                value={config.condition || ''}
                onChange={(e) => handleConfigChange('condition', e.target.value)}
                placeholder="variable_name"
              />
            </div>
            <div className="form-group">
              <label>Operator</label>
              <select
                value={config.operator || '=='}
                onChange={(e) => handleConfigChange('operator', e.target.value)}
              >
                <option value="==">=</option>
                <option value="!=">!=</option>
                <option value="<">&lt;</option>
                <option value=">">&gt;</option>
                <option value="<=">&lt;=</option>
                <option value=">=">&gt;=</option>
                <option value="contains">contains</option>
                <option value="matches">matches</option>
              </select>
            </div>
            <div className="form-group">
              <label>Value</label>
              <input
                type="text"
                value={config.value || ''}
                onChange={(e) => handleConfigChange('value', e.target.value)}
                placeholder="expected_value"
              />
            </div>
          </div>
        );
      }

      case 'transformer': {
        return (
          <div className="config-fields">
            <div className="form-group">
              <label>Transform Type</label>
              <select
                value={(node.config as any).transformationType || 'map'}
                onChange={(e) => handleConfigChange('transformationType', e.target.value)}
              >
                <option value="map">Map</option>
                <option value="filter">Filter</option>
                <option value="aggregate">Aggregate</option>
                <option value="custom">Custom</option>
              </select>
            </div>
            {(node.config as any).transformationType === 'custom' && (
              <div className="form-group">
                <label>Code</label>
                <textarea
                  value={(node.config as any).code || ''}
                  onChange={(e) => handleConfigChange('code', e.target.value)}
                  placeholder="// JavaScript code"
                  rows={6}
                />
              </div>
            )}
          </div>
        );
      }

      case 'logic_switch': {
        const config = node.config as any;
        return (
          <div className="config-fields">
            <div className="form-group">
              <label>Variable</label>
              <input
                type="text"
                value={config.variable || ''}
                onChange={(e) => handleConfigChange('variable', e.target.value)}
                placeholder="variable_name"
              />
            </div>
            <div className="form-group">
              <label>Cases</label>
              <div className="cases-list">
                {config.cases?.map((c: any, idx: number) => (
                  <div key={idx} className="case-item">
                    <input
                      type="text"
                      value={c.value}
                      placeholder="case value"
                      onChange={(e) => {
                        const newCases = [...config.cases];
                        newCases[idx].value = e.target.value;
                        handleConfigChange('cases', newCases);
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      }

      case 'validator': {
        return (
          <div className="config-fields">
            <div className="form-group">
              <label>Validation Rules</label>
              <textarea
                value={JSON.stringify((node.config as any).rules || [], null, 2)}
                onChange={(e) => {
                  try {
                    handleConfigChange('rules', JSON.parse(e.target.value));
                  } catch {
                    // Invalid JSON
                  }
                }}
                placeholder='[{"field": "name", "rule": "required"}]'
                rows={6}
              />
            </div>
          </div>
        );
      }

      case 'output': {
        const config = node.config as any;
        return (
          <div className="config-fields">
            <div className="form-group">
              <label>Format</label>
              <select
                value={config.format || 'json'}
                onChange={(e) => handleConfigChange('format', e.target.value)}
              >
                <option value="json">JSON</option>
                <option value="csv">CSV</option>
                <option value="xml">XML</option>
                <option value="plain_text">Plain Text</option>
              </select>
            </div>
          </div>
        );
      }

      case 'logic_loop': {
        const config = node.config as any;
        return (
          <div className="config-fields">
            <div className="form-group">
              <label>Iteration Variable</label>
              <input
                type="text"
                value={config.iterationVariable || 'item'}
                onChange={(e) => handleConfigChange('iterationVariable', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Array Variable</label>
              <input
                type="text"
                value={config.arrayVariable || ''}
                onChange={(e) => handleConfigChange('arrayVariable', e.target.value)}
                placeholder="array_name"
              />
            </div>
            <div className="form-group">
              <label>Max Iterations</label>
              <input
                type="number"
                value={config.maxIterations || 1000}
                onChange={(e) => handleConfigChange('maxIterations', parseInt(e.target.value))}
              />
            </div>
          </div>
        );
      }

      default:
        return <div className="config-fields"><p>No configuration available</p></div>;
    }
  };

  return (
    <div className="config-panel">
      <div className="panel-header">
        <h3>Node Configuration</h3>
      </div>

      <div className="panel-content">
        <div className="form-group">
          <label>Node Name</label>
          <input
            type="text"
            value={node.name}
            onChange={(e) => handleNameChange(e.target.value)}
            className="node-name-input"
          />
        </div>

        <div className="form-group">
          <label>Type</label>
          <div className="type-badge">{node.type}</div>
        </div>

        <hr style={{ margin: '16px 0', border: 'none', borderTop: '1px solid #e0e0e0' }} />

        {renderConfigUI()}

        {node.inputPorts.length > 0 && (
          <>
            <hr style={{ margin: '16px 0', border: 'none', borderTop: '1px solid #e0e0e0' }} />
            <div className="ports-info">
              <h4>Input Ports</h4>
              {node.inputPorts.map((port) => (
                <div key={port.id} className="port-info">
                  <span className="port-dot" />
                  <span>{port.label}</span>
                </div>
              ))}
            </div>
          </>
        )}

        {node.outputPorts.length > 0 && (
          <>
            <hr style={{ margin: '16px 0', border: 'none', borderTop: '1px solid #e0e0e0' }} />
            <div className="ports-info">
              <h4>Output Ports</h4>
              {node.outputPorts.map((port) => (
                <div key={port.id} className="port-info">
                  <span>{port.label}</span>
                  <span className="port-dot" />
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Model Node Configuration Component with Dropdown
interface ModelNodeConfigProps {
  config: ModelConfig;
  onConfigChange: (key: string, value: any) => void;
  nodeId: string;  // Add nodeId to access context if needed
}

const ModelNodeConfig: React.FC<ModelNodeConfigProps> = ({ config, onConfigChange, nodeId }) => {
  const [models, setModels] = useState<Model[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { updateNode } = usePipelineBuilder();

  useEffect(() => {
    loadModels();
  }, []);

  const loadModels = async () => {
    setLoading(true);
    setError(null);
    try {
      const modelList = await modelService.listModels();
      setModels(modelList);
      
      // If a model is already selected, ensure it's still in the list
      if (config.modelId && !modelList.find(m => m.id === config.modelId)) {
        // Model might have been deleted, clear selection
        updateNode(nodeId, {
          config: {
            ...config,
            modelId: '',
            modelName: ''
          }
        });
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load models');
      console.error('Error loading models:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleModelSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const modelId = e.target.value;
    const selectedModel = models.find(m => m.id === modelId);
    
    // Update the entire config at once using updateNode directly
    const newConfig: ModelConfig = {
      ...config,
      modelId: modelId || '',
      modelName: selectedModel ? (selectedModel.display_name || selectedModel.name) : '',
      version: config.version || '1.0',
      params: config.params || {}
    };
    
    // Use updateNode to update the entire config at once
    updateNode(nodeId, {
      config: newConfig
    });
  };

  return (
    <div className="config-fields">
      <div className="form-group">
        <label>Select Model</label>
        {loading ? (
          <div className="loading-text">Loading models...</div>
        ) : error ? (
          <div className="error-text">
            {error}
            <button onClick={loadModels} style={{ marginLeft: '8px', fontSize: '12px' }}>
              Retry
            </button>
          </div>
        ) : (
          <select
            value={config.modelId || ''}
            onChange={handleModelSelect}
            className="model-select"
          >
            <option value="">-- Select a model --</option>
            {models.map((model) => (
              <option key={model.id} value={model.id}>
                {model.display_name || model.name}
              </option>
            ))}
          </select>
        )}
      </div>
      {config.modelId && (
        <>
          <div className="form-group">
            <label>Model ID</label>
            <input
              type="text"
              value={config.modelId || ''}
              readOnly
              className="readonly-input"
            />
          </div>
          <div className="form-group">
            <label>Model Name</label>
            <input
              type="text"
              value={config.modelName || ''}
              readOnly
              className="readonly-input"
            />
          </div>
        </>
      )}
      <div className="form-group">
        <label>Version</label>
        <input
          type="text"
          value={config.version || '1.0'}
          onChange={(e) => onConfigChange('version', e.target.value)}
        />
      </div>
    </div>
  );
};

export default PipelineNodeConfigPanel;
